import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import ChangeManagementDialog, {
  emptyChangeManagementRow,
} from '../../shared/ChangeManagementDialog';
import { toDatetimeLocalValue } from '../../shared/DeploymentLifecycleFields';
import { apiErrorMessage } from '../../utils/responseHelpers';
import { isChangeLocked } from './changeApprovalState';

const dateTimeFields = [
  'requestedDate',
  'scheduledStart',
  'scheduledEnd',
  'actualStart',
  'actualEnd',
];

/**
 * Edit dialog for /system-register/change-managements.
 * Compatible with EntityCrudPage EditDialog props.
 */
export default function EditChangeManagementDialog({
  open,
  onClose,
  onSuccess,
  row,
  updateItem,
}) {
  const [saving, setSaving] = useState(false);

  const value = useMemo(() => {
    if (!row) return emptyChangeManagementRow();
    const next = {
      ...emptyChangeManagementRow(),
      ...row,
      softwareId: row.softwareId ?? null,
      id: row.id ?? null,
      approvals: Array.isArray(row.approvals) ? row.approvals : [],
    };
    dateTimeFields.forEach((field) => {
      next[field] = toDatetimeLocalValue(row[field]);
    });
    return next;
  }, [row]);

  const handleSave = async (values) => {
    if (!row?.id || row?.softwareId == null) {
      toast.error('Change management record is incomplete.');
      return;
    }
    if (!(values?.title || '').trim()) {
      toast.error('Title is required.');
      return;
    }
    if (isChangeLocked(row) || isChangeLocked(values)) {
      toast.error('This change is fully approved and cannot be edited.');
      return;
    }
    setSaving(true);
    try {
      await updateItem(row, {
        ...values,
        softwareId: row.softwareId,
        id: row.id,
        implementationStatus: values.implementationStatus || row.implementationStatus || 'PLANNED',
      });
      toast.success('Change management updated');
      onSuccess?.();
      onClose?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to update change management.'));
      throw error;
    } finally {
      setSaving(false);
    }
  };

  if (!row) return null;

  return (
    <ChangeManagementDialog
      open={open}
      mode="edit"
      selectSoftware={false}
      value={value}
      saving={saving}
      onClose={() => {
        if (saving) return;
        onClose?.();
      }}
      onSave={handleSave}
    />
  );
}
